import { EntityUtils } from '@record-it-npm/synchro-client';
import React, { useCallback, VoidFunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { FixedButton } from 'record-it-components/button/FixedButton/FixedButton';
import { useEventCallback } from 'record-it-components/hooks/useEventCallback';
import { useModalOpened } from 'record-it-components/hooks/useModalOpened/useModalOpened';
import { Icon, IconType } from 'record-it-components/misc/Icon/Icon';
import { SelectModal } from 'record-it-components/modal/SelectModal/SelectModal';
import { prepareProps } from 'record-it-components/utils/propUtils';
import { PropsWithoutChildren } from 'record-it-components/utils/utilityTypes';
import { EntityName } from 'record-it-time-common/EntityName';
import { assertNotNullOrUndefined } from 'record-it-utils/utils/assert/assertNotNullOrUndefined';
import { UserGroup } from '../../../classes/entities/UserGroup/userGroupTypes';
import { useEntities } from '../../../hooks/entity/useEntities/useEntities';
import { useEntityRepository } from '../../../hooks/entity/useEntityRepository';
import { useStopwatchManager } from '../../../hooks/stopwatch/useStopwatchManager';

export type StartNewStopwatchFixedButtonProps = PropsWithoutChildren<{
  'data-testid'?: string;
}>;

export const StartNewStopwatchFixedButton: VoidFunctionComponent<StartNewStopwatchFixedButtonProps> =
  React.memo((props) => {
    const { 'data-testid': dataTestid } = prepareProps({ props });

    const { t } = useTranslation();
    const manager = useStopwatchManager();

    const {
      open: openSelectUserGroupModal,
      close: closeSelectUserGroupModal,
      modalProps
    } = useModalOpened({ closeOnBackDropClick: true });

    const userGroupRepository = useEntityRepository(EntityName.UserGroup);
    const handleAddStopwatchClick = useEventCallback(() => {
      const userGroups = userGroupRepository.getAll();
      if (userGroups.length === 1) {
        const firstUserGroup = userGroups[0];
        assertNotNullOrUndefined(
          firstUserGroup,
          'userGroups have a length but not first item? wtf'
        );
        manager.startNew(firstUserGroup.id);
      } else if (userGroups.length > 1) {
        openSelectUserGroupModal();
      } else {
        throw new Error('no usergroup available');
      }
    });

    const userGroups = useEntities(EntityName.UserGroup, 'getAll');
    const renderContentOfUserGroup = useCallback(
      (userGroup: UserGroup) => <span>{userGroup.name}</span>,
      []
    );
    const getKeyOfUserGroup = useCallback((userGroup: UserGroup) => {
      return EntityUtils.getTrackingKey(userGroup);
    }, []);

    const handleUserGroupSelected = useEventCallback((userGroup: UserGroup) => {
      manager.startNew(userGroup.id);
      closeSelectUserGroupModal();
    });

    return (
      <>
        <FixedButton
          onClick={handleAddStopwatchClick}
          data-testid={dataTestid ?? 'StartNewStopwatchFixedButton.Button'}
        >
          {t(
            'components.timeTracking.StartNewStopwatchFixedButton.startNewStopwatch'
          )}
          <Icon
            name="plus-circle"
            type={IconType.Light}
            hasTextOnLeftSide={true}
          ></Icon>
        </FixedButton>
        <SelectModal<UserGroup>
          {...modalProps}
          label={t(
            'components.timeTracking.StartNewStopwatchFixedButton.userGroupSelectionTitle'
          )}
          items={userGroups}
          getKeyOfItem={getKeyOfUserGroup}
          renderContentOfItem={renderContentOfUserGroup}
          onCloseButtonClick={closeSelectUserGroupModal}
          onItemSelected={handleUserGroupSelected}
        ></SelectModal>
      </>
    );
  });
